import React from 'react';
import './Statblocker.css';

const Statblocker = ({ monsterData }) => {
  // Format traits for display
  const formatTraits = (traitsList) => {
    if (!traitsList || traitsList.length === 0) return null;
    
    return traitsList.map((trait, index) => (
      <div key={index} className="property-block">
        <h4>{trait.name}.</h4>
        <p dangerouslySetInnerHTML={{ __html: formatDescription(trait.desc) }}></p>
      </div>
    ));
  };

  // Format descriptive text with italics and other formatting
  const formatDescription = (desc) => {
    if (!desc) return '';
    
    // Replace _text_ with <i>text</i> for italics
    let formattedDesc = desc.replace(/_([^_]+)_/g, '<i>$1</i>');
    
    return formattedDesc;
  };

  // Simplified speed function that takes a string directly
  const getSpeedString = () => {
    return monsterData.speed || "—";
  };

  // Update the getAllTraits function to add a header for passives
  const getAllTraits = () => {
    const allTraits = [
      // Add "Passives" header before passives
      ...(monsterData.passives && monsterData.passives.length > 0 ? 
          [{ name: "Passives", isHeader: true }, ...monsterData.passives.map(passive => ({...passive, isPassive: true}))] : []),
      // Add "Traits" header before abilities
      ...(monsterData.abilities && monsterData.abilities.length > 0 ? 
          [{ name: "Traits", isHeader: true }, ...monsterData.abilities] : []),
      ...(monsterData.actions ? [{ name: "Actions", isHeader: true }, ...monsterData.actions] : []),
      ...(monsterData.bonusActions && monsterData.bonusActions.length > 0 ? 
          [{ name: "Bonus Actions", isHeader: true }, ...monsterData.bonusActions] : []),
      ...(monsterData.reactions && monsterData.reactions.length > 0 ? 
          [{ name: "Reactions", isHeader: true }, ...monsterData.reactions] : []),
    ];

    if (monsterData.isLegendary && (monsterData.legendaries && monsterData.legendaries.length > 0 || 
        monsterData.legendariesDescription)) {
      allTraits.push({ name: "Legendary Actions", isHeader: true, description: monsterData.legendariesDescription });
      allTraits.push(...(monsterData.legendaries || []));
    }
    
    return allTraits;
  };

  // Update the renderTraitSection function to show lines between sections but not at the very top
  const renderTraitSection = (traits) => {
    if (!traits || traits.length === 0) return null;
    
    // Track if this is the first header we've encountered
    let isFirstHeader = true;
    
    return traits.map((trait, index) => {
      if (trait.isHeader) {
        // Only show h3 headers for Actions, Bonus Actions, Reactions, and Legendary Actions
        // Don't show headers for Passives or Traits
        const shouldShowHeader = !["Passives", "Traits"].includes(trait.name);
        
        // Store current value of isFirstHeader
        const isFirst = isFirstHeader;
        // Set isFirstHeader to false after processing first header
        isFirstHeader = false;
        
        return (
          <React.Fragment key={`header-${index}`}>
            {/* Only show the line if this is NOT the first header */}
            {!isFirst && (
              <svg height="2" width="100%" className="straight-rule">
                <line x1="0" y1="1" x2="400" y2="1" stroke="#e8a823" strokeWidth="2" />
              </svg>
            )}
            
            {/* Only show the header text for action sections */}
            {shouldShowHeader && (
              <h3>{trait.name}</h3>
            )}
            
            {trait.description && (
              <div className="property-block">
                <p dangerouslySetInnerHTML={{ __html: formatDescription(trait.description) }}></p>
              </div>
            )}
          </React.Fragment>
        );
      }
      
      return (
        <div key={`trait-${index}`} className={`property-block ${trait.isPassive ? 'passive-block' : ''}`}>
          <p>
            <strong>{trait.name}.</strong>{" "}
            <span dangerouslySetInnerHTML={{ __html: formatDescription(trait.desc) }}></span>
          </p>
        </div>
      );
    });
  };

  if (!monsterData) {
    return <div className="stat-block">No monster data provided</div>;
  }

  const monsterTypeString = `${monsterData.size.charAt(0).toUpperCase() + monsterData.size.slice(1)} ${monsterData.type}${monsterData.tag ? ` (${monsterData.tag})` : ''}, ${monsterData.alignment}`;

  return (
    <div className="stat-block-wrapper">
      <div className="stat-block">
        <div className="section-left">
          <div className="creature-heading">
            <h1>{monsterData.name}</h1>
            <h2>{monsterTypeString}</h2>
          </div>
          <svg height="2" width="100%" className="straight-rule">
            <line x1="0" y1="1" x2="400" y2="1" stroke="#e8a823" strokeWidth="2" />
          </svg>
          <div className="top-stats">
            <div className="property-line first">
              <h4>Armor Class</h4>
              <p>{monsterData.armorClass}{monsterData.armorName ? ` (${monsterData.armorName})` : ""}</p>
            </div>
            <div className="property-line">
              <h4>Hit Points</h4>
              <p>{monsterData.hp || "—"}</p>
            </div>
            <div className="property-line">
              <h4>Speed</h4>
              <p>{getSpeedString()}</p>
            </div>
            <div className="property-line last">
              <h4>Initiative</h4>
              <p>{monsterData.initiative || "—"}</p>
            </div>
            <svg height="2" width="100%" className="straight-rule">
              <line x1="0" y1="1" x2="400" y2="1" stroke="#e8a823" strokeWidth="2" />
            </svg>
            <div className="scores">
              <div className="scores-strength">
                <h4>STR</h4>
                <p>{monsterData.strPoints}</p>
              </div>
              <div className="scores-dexterity">
                <h4>DEX</h4>
                <p>{monsterData.dexPoints}</p>
              </div>
              <div className="scores-constitution">
                <h4>CON</h4>
                <p>{monsterData.conPoints}</p>
              </div>
              <div className="scores-intelligence">
                <h4>INT</h4>
                <p>{monsterData.intPoints}</p>
              </div>
              <div className="scores-wisdom">
                <h4>WIS</h4>
                <p>{monsterData.wisPoints}</p>
              </div>
              <div className="scores-charisma">
                <h4>CHA</h4>
                <p>{monsterData.chaPoints}</p>
              </div>
            </div>
            <svg height="2" width="100%" className="straight-rule">
              <line x1="0" y1="1" x2="400" y2="1" stroke="#e8a823" strokeWidth="2" />
            </svg>

            {/* Removed Saving Throws, Skills, and Damage Vulnerabilities sections */}
            
          </div> {/* End top stats */}
          
          <div className="actions">
            <div className="traits-list">
              {renderTraitSection(getAllTraits())}
            </div>
          </div>
        </div> {/* End section left */}
      </div> {/* End stat block */}
    </div>
  );
};

export default Statblocker;